'use client';

import {
  useApiClient,
  type WebshopReviewMine,
  WebshopReviewQueries,
  webshopReviewGetMyReviewsService,
} from '@cldsystems/ws-api-client';
import { useQueries } from '@tanstack/react-query';
import { useAppContext } from '@/providers/app-provider/app-provider';
import { useKeycloak } from '@/providers/keycloak-provider';
import { resolveMyReview } from '@/utils/product-review-rating';

/**
 * A product is a per-locale record, and a review is stored against exactly one of
 * those ids — it carries no SKU or locale of its own. So every review read has to
 * fan out over the sibling ids for the SKU, or each language only ever sees the
 * reviews written while that language was selected.
 */
export const buildReviewQueryKey = (
  queryName: WebshopReviewQueries,
  queries: Record<string, string>,
) =>
  // Mirrors the key shape the generated hooks use — `[name, params, queries]` —
  // so these share a cache with them and the prefix invalidations still land.
  [queryName, undefined, queries] as const;

export type MyProductReview = WebshopReviewMine & {
  /** The locale-specific product this review is filed against. */
  productId: string;
};

type MyProductReviewResult = {
  /** The review to show as "your review", if the user has one in any locale. */
  myReview: MyProductReview | undefined;
  /** Set whenever the user has reviewed this product, even if that review is hidden. */
  userId: string | undefined;
};

/**
 * Finds the signed-in user's review for a product across every locale it exists in.
 *
 * Prefers the review filed against the locale being viewed — `productIds` leads
 * with it — so editing normally touches the record in front of the user, and falls
 * back to whichever other locale has one.
 */
const useMyProductReview = (productIds: string[]): MyProductReviewResult => {
  const { isStudioPreview } = useAppContext<{ isStudioPreview?: boolean }>();
  const { isAuthenticated } = useKeycloak();
  const { httpClient, hasSchema } = useApiClient();

  return useQueries({
    queries: productIds.map(productId => ({
      queryKey: buildReviewQueryKey(WebshopReviewQueries.GetMyReviews, { productId }),
      queryFn: webshopReviewGetMyReviewsService({
        queries: { productId },
        httpClient,
        hasSchema: Boolean(hasSchema),
      }),
      enabled: isAuthenticated && !isStudioPreview && Boolean(httpClient),
    })),
    combine: (results): MyProductReviewResult => {
      const index = results.findIndex(result => result.data?.[0]);
      if (index === -1) return { myReview: undefined, userId: undefined };

      const mine = results[index].data![0];
      const resolved = resolveMyReview(mine);

      return {
        myReview: resolved ? { ...resolved, productId: productIds[index] } : undefined,
        // Kept even when the review resolves away, so the public list still
        // filters out the user's own entries.
        userId: mine.user.id,
      };
    },
  });
};

export default useMyProductReview;
